import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  AuthenticatedUser,
  AuthResponse,
  UserType,
} from "src/network/responseTypes/authTypes";
import { loginApi, registerApi } from "src/network/api";
import { LOCAL_AUTH_STATE_KEY } from "src/utils/appConstants";

// dispatch(userLogin({email;}))
///action
export const userLoginAction = createAsyncThunk<
  AuthResponse,
  UserLoginParams,
  {
    rejectValue: BasicAPIResponse;
  }
>("auth/login", async (params, thunkAPI) => {
  try {
    const response = await loginApi({
      email: params.email,
      password: params.password,
      type: params.type,
    });
    if (response.status === 200 && response.data.status === 200) {
      return thunkAPI.fulfillWithValue(response.data as AuthResponse);
    } else {
      return thunkAPI.rejectWithValue(response.data as BasicAPIResponse);
    }
  } catch (error) {
    return thunkAPI.rejectWithValue({
      status: 500,
      message: "Something went wrong, please try again.",
    });
  }
});

export const userRegisterAction = createAsyncThunk<
  AuthResponse,
  UserRegisterParams,
  {
    rejectValue: BasicAPIResponse;
  }
>("auth/register", async (params, thunkAPI) => {
  try {
    const response = await registerApi({
      name: params.name,
      email: params.email,
      password: params.password,
      contact: params.contact,
      type: params.type,
    });
    if (response.status === 200 && response.data.status === 200) {
      return thunkAPI.fulfillWithValue(response.data as AuthResponse);
    } else {
      return thunkAPI.rejectWithValue(response.data as BasicAPIResponse);
    }
  } catch (error) {
    return thunkAPI.rejectWithValue({
      status: 500,
      message: "Something went wrong, please try again.",
    });
  }
});

export type AuthStateT = {
  loading: boolean;
  user?: AuthenticatedUser;
  token?: String;
  error?: String;
  linkedToken?: String;
  activeAccount?: UserType;
};

const initialState: AuthStateT = {
  loading: false,
  user: null,
  token: null,
  linkedToken: null,
  activeAccount: null,
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    userLogout(state) {
      state.user = null;
      state.token = null;
      state.linkedToken = null;
      state.activeAccount = null;
      state.loading = false;
      state.error = null;
      localStorage.clear();
    },
    setActiveAccount(state, action) {
      state.activeAccount = action.payload;
    },
    updateUserDetails(state, action) {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userLoginAction.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(userLoginAction.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.auth_token;
      state.activeAccount = action.payload.user.type;
      if (action.payload?.linked_token) {
        state.linkedToken = action.payload.linked_token;
      }
      toast.success("Loggedin successfully");
    });

    builder.addCase(userLoginAction.rejected, (state, action) => {
      toast.error(action.payload.message);
      state.loading = false;
      state.error = action.payload.message;
    });

    builder.addCase(userRegisterAction.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(userRegisterAction.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.auth_token;
      state.activeAccount = action.payload.user.type;
      localStorage.setItem("auth_token", action.payload.auth_token.toString());
      toast.success("Registered Successfully");
    });

    builder.addCase(userRegisterAction.rejected, (state, action) => {
      toast.error(action.payload?.message || "Registration failed");
      state.loading = false;
      state.error = action.payload?.message;
    });
  },
});

export const authReducer = authSlice.reducer;
export const { userLogout, updateUserDetails, setActiveAccount } =
  authSlice.actions;
